import React, { useState } from "react";

import { CalendarIcon } from "@packages/ds";
import { useIntl } from "react-intl";

import DateRangePicker from "Components/DateRangePicker/DateRangePicker";
import * as Dropdown from "ds/Dropdown";
import { Tooltip } from "ds/Tooltip";
import useMetricsMaxDuration from "src/organization/pages/project/environment/metrics/hooks/useMetricsMaxDuration";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { parseTimeDuration } from "src/organization/pages/project/environment/metrics/utils/parseDimensions/parseDimensions";
import { ParseTimeDurationType } from "src/organization/pages/project/environment/metrics/utils/parseDimensions/types";

import { timeframeToRange } from "../Chart/settings";

import SyncTab, { TabSyncProps } from "./SyncTab";
import * as S from "./TimeframeSelector.styles";
import { getDateFormat, getNowFormat } from "./utils";

const SECONDS_IN_DAY = 86400;

type Props = {
  onChange: (p: { from: Date; to: Date } | string) => void;
  selectedTimeframe: TimeFrameType;
  timeframes: Array<Omit<TimeFrameType, "from" | "to">>;
  from?: Date;
  to?: Date;
  rangeFormatter?: (timeframe: TimeFrameType) => string;
  tabSync?: TabSyncProps;
  timeDuration?: ParseTimeDurationType;
};

const TimeFrameSelector = (props: Props) => {
  const {
    timeframes = [],
    selectedTimeframe,
    from,
    to,
    onChange,
    rangeFormatter,
    tabSync,
    timeDuration
  } = props;

  const intl = useIntl();
  let maxDuration = useMetricsMaxDuration();
  maxDuration = timeDuration || maxDuration;

  const [isOpen, setIsOpen] = useState(false);

  const onDateChange = ({ start, end }: Partial<{ start; end }>) => {
    onChange({ from: start, to: end });
    setIsOpen(!isOpen);
    return true;
  };

  const toggleOpenRangePicker = () => {
    setIsOpen(!isOpen);
  };

  const { start, end } = timeframeToRange({ from, to, ...selectedTimeframe });

  const formattedRange = rangeFormatter
    ? rangeFormatter({ ...selectedTimeframe, from: start, to: end })
    : selectedTimeframe.id
      ? getNowFormat()
      : getDateFormat(start, end);

  return (
    <S.Layout>
      <S.List>
        {timeframes.map(timeframe => {
          const { seconds } = parseTimeDuration(timeframe.id);
          if (seconds > maxDuration.seconds)
            // Don't show ranges higher than reported in capabilites
            return null;
          return (
            <S.ListItem key={timeframe.id}>
              <S.Toggle
                isActive={timeframe.id === selectedTimeframe.id}
                onClick={() => {
                  onChange(timeframe.id as string);
                }}
              >
                {timeframe.label}
              </S.Toggle>
            </S.ListItem>
          );
        })}
      </S.List>

      <S.Divider />

      <Tooltip
        tooltip={intl.formatMessage(
          {
            id: "data_available_duration"
          },
          { duration: maxDuration.durationText }
        )}
      >
        {maxDuration.seconds > SECONDS_IN_DAY ? (
          <Dropdown.Root open={isOpen} onOpenChange={toggleOpenRangePicker}>
            <S.DropdownTrigger variant="mixed">
              <S.Toggle isActive={!selectedTimeframe.id} interactive>
                <CalendarIcon className="calendar" />
                {formattedRange}
              </S.Toggle>
            </S.DropdownTrigger>
            <Dropdown.Portal>
              <Dropdown.Content align="end">
                <DateRangePicker
                  onSubmit={onDateChange}
                  onCancel={toggleOpenRangePicker}
                  range={{
                    start,
                    end
                  }}
                  dualView={false}
                  maxDuration={maxDuration}
                  navigable={maxDuration.seconds > SECONDS_IN_DAY * 30 * 3}
                />
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
        ) : (
          <S.TimeBlock isActive>{formattedRange}</S.TimeBlock>
        )}
      </Tooltip>

      <S.Divider />
      <SyncTab
        onSyncChange={tabSync?.onSyncChange}
        enabled={!!tabSync}
        canJoinSync={tabSync?.canJoinSync}
        isSynced={tabSync?.isSynced}
      />
    </S.Layout>
  );
};

export default React.memo(TimeFrameSelector);
